<template>
  <div class="mt-1">
    <p class="last-sending">{{ lastLabel }}</p>
    <b-table
      :busy="isLoading"
      head-variant="light"
      :items="items"
      :fields="fields"
      :no-local-sorting="true"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @sort-changed="onSortChanged"
      no-border-collapse
      responsive
      show-empty
      small
    >
      <template #table-busy>
        <table-skeleton />
      </template>
      <template #empty>
        <empty-table :isTableInModal="true" />
      </template>
      <template #cell(actions)="{ item }">
        <div class="action-buttons">
          <b-button
            size="sm"
            variant="lisaweb"
            @click="openDetail(item)"
            title="Dettaglio Log"
          >
            <base-icon name="eye" width="22"></base-icon>
          </b-button>
        </div>
      </template>
      <template #cell(communication_procedure)="{ value }">
        <router-link
          v-if="canSeeProcedure(value)"
          :to="{
            name: 'communication_procedures.index',
            query: { id: value.id },
          }"
          title="Apri la procedura"
          >{{ procedureLabel(value) }}</router-link
        >
        <span v-else>{{ procedureLabel(value) }}</span>
      </template>
      <template #cell(status)="{ value }">
        <status-badge kind="log" :status="logStatus(value)" />
      </template>
      <template #cell(error_message)="{ value }">
        <span :title="value">{{ value }}</span>
      </template>
      <template #cell()="{ value }">
        <span>{{ value }}</span>
      </template>
    </b-table>
    <b-modal
      :id="modalId"
      size="xl"
      scrollable
      ok-only
      ok-title="Chiudi"
      ok-variant="-transparent"
      header-bg-variant="lisaweb"
      no-close-on-backdrop
    >
      <template v-slot:modal-header-close>
        <base-icon name="close" width="14" height="14" />
      </template>
      <log-detail
        v-if="logId"
        :key="logId"
        :resourceId="logId"
        :registryId="resourceId"
      />
    </b-modal>
  </div>
</template>

<script>
// Scheda "Comunicazioni" del dettaglio cliente: i messaggi che LisaComunica gli
// ha inviato (procedure, analisi e Compleanni), con le colonne della scheda Invii più
// l'origine; al clic il Dettaglio Log di Lisacomunica > Log.
import BaseIcon from "@/components/BaseIcon";
import StatusBadge from "@/components/StatusBadge";
import EmptyTable from "@/components/EmptyTable.vue";
import LogDetail from "@/components/profile/lisacomunica/logs/details/General";
import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import { canSeeAllCommunications, canVerb } from "@/utils/permissions";
import { formatSentAt, sendingRows } from "@/utils/sendings";
import {
  channelLabel,
  lastSentLabel,
  logStatus,
} from "@/utils/communications";

export default {
  name: "Communications",
  inheritAttrs: false, // dbRowData della modale: non serve e non va sul div
  props: {
    resourceId: Number,
  },
  components: {
    BaseIcon,
    StatusBadge,
    EmptyTable,
    LogDetail,
  },
  data() {
    return {
      isLoading: false,
      items: [],
      sortBy: "sent_at",
      sortDesc: true,
      // l'ultima richiesta partita: le risposte di quelle precedenti si scartano
      lastRequest: 0,
      logId: null,
      modalId: `registry-communication-${this._uid}`,
      fields: [
        {
          key: "actions",
          label: "",
          thClass: "align-middle actions-head",
          tdClass: "actions",
        },
        // ordina il backend (sort[byAttribute]), come le altre tabelle
        {
          key: "sent_at",
          label: "Data",
          formatter: formatSentAt,
          sortable: true,
        },
        {
          key: "origin",
          label: "Origine",
          formatter: (value) => value?.text || "-",
          sortable: true,
        },
        { key: "communication_procedure", label: "Procedura" },
        {
          key: "user.formatted_title",
          label: "Effettuato da",
          formatter: (value) => value || "-",
        },
        {
          key: "channel",
          label: "Canale",
          formatter: channelLabel,
          sortable: true,
        },
        { key: "recipient", label: "Recapito" },
        { key: "status", label: "Esito" },
        {
          key: "error_message",
          label: "Messaggio di errore",
          formatter: (value) => value || "-",
        },
      ],
    };
  },
  computed: {
    lastLabel() {
      return this.isLoading ? "" : lastSentLabel(this.items);
    },
  },
  methods: {
    logStatus,
    // Come nella scheda Invii: la procedura la apre chi l'ha creata, l'admin o
    // chi vede le procedure di tutti.
    canSeeProcedure(procedure) {
      if (!procedure?.id || !canVerb("communication_procedures", "index"))
        return false;
      return (
        canSeeAllCommunications() ||
        Number(procedure.user_id) === Number(this.$store.state.auth.user?.id)
      );
    },
    procedureLabel(procedure) {
      if (!procedure) return "-";
      return `#${procedure.id} ${procedure.description || ""}`.trim();
    },
    onSortChanged({ sortBy, sortDesc }) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.fetch();
    },
    openDetail(item) {
      this.logId = item.id;
      this.$bvModal.show(this.modalId);
    },
    fetch() {
      // Cambiando ordinamento o cliente durante una richiesta, la più lenta
      // non deve sovrascrivere la tabella (es. con i messaggi di un altro cliente).
      const request = ++this.lastRequest;
      this.isLoading = true;
      RepositoryFactory.get("registry")
        .communications(
          this.resourceId,
          `sort[byAttribute]=${this.sortBy}&dir[byAttribute]=${
            this.sortDesc ? "desc" : "asc"
          }`
        )
        .then((response) => {
          if (request !== this.lastRequest) return;
          this.items = sendingRows(response.data);
        })
        .catch((error) => {
          if (request !== this.lastRequest) return;
          this.items = [];
          this.$showSnackbar({
            preset: "error",
            text: `${this.$getErrorMessage(error)}`,
          });
        })
        .finally(() => {
          if (request === this.lastRequest) this.isLoading = false;
        });
    },
  },
  watch: {
    resourceId() {
      this.fetch();
    },
  },
  mounted() {
    this.fetch();
  },
};
</script>
